import { __assign, __spreadArray } from "tslib";
// DS: C125, C125_m
import React, { useState, useMemo, useRef, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { PremiumTrash } from '@wix/da-ds/pkg/Icons/Confirmations';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import noop from '../../../../utils/noop';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { useDeviationAttributes } from '../../../hooks/useDeviationAttributes';
import UserAvatar from '../../../../User/Avatar';
import UserLink from '../../../../User/Link';
// TODO: move this out of WatchSuggestions
import UserWatchGhostButton from '../../../../WatchSuggestions/UserWatchGhostButton';
import Timestamp from '../../../../Timestamp';
import ConfirmationModal from '../../../../Popup/ConfirmationModal';
import { getDeviationEntityId } from '../../../../redux/normalizr/schemas/deviation';
import useDuperbrowseLink from '../../../../Duperbrowse/hooks/useDuperbrowseLink';
import { DeviationLink } from '../../../../Duperbrowse';
import ButtonB5 from '../../../../Button/ButtonB5';
import ThumbActions from '../../../Helper/ThumbActions';
import { getDeviationType, getDeviationUrl, deviationTypeToTranslationKey, getDeviationResourceType, } from '../../../_helpers';
import PriceAndSubscribeButton from '../PriceAndSubscribeButton';
import s from './FeedCardHeader.scss';
var subscriptionReferralType = { referral_info: ReferralType.LOCKED_CONTENT };
var FeedCardHeader = function (_a) {
    var deviation = _a.deviation, className = _a.className, _b = _a.extraActions, extraActions = _b === void 0 ? [] : _b, disableScrollRestoration = _a.disableScrollRestoration, _c = _a.withWatchOrSubscribeButton, withWatchOrSubscribeButton = _c === void 0 ? true : _c, _d = _a.showReportModal, showReportModal = _d === void 0 ? noop : _d, _e = _a.deleteDeviation, deleteDeviation = _e === void 0 ? noop : _e;
    var t = useTranslation().t;
    var logBiEvent = useBiLogger();
    var isDesktop = !useContext(MobileContext);
    var _f = useState(false), isShowingConfirmation = _f[0], setShowingConfirmation = _f[1];
    var _g = useSubscriptionTier(deviation), isOwner = _g.isOwner, tier = _g.tier, needsSubscription = _g.needsSubscription;
    var type = getDeviationType(deviation);
    var typeName = t(deviationTypeToTranslationKey(type));
    // add standard post actions: report and go to page
    var isReportable = useDeviationAttributes(deviation, isOwner).isReportable;
    // duperbrowse for the canonical link
    var duperRef = useRef(null);
    var onDuperbrowseClick = useDuperbrowseLink(getDeviationEntityId(deviation), duperRef, undefined, disableScrollRestoration);
    var watchOrSubscribeButton = null;
    if (isDesktop && withWatchOrSubscribeButton) {
        watchOrSubscribeButton =
            !!tier && needsSubscription ? (React.createElement(BiLoggerContextProvider, { value: subscriptionReferralType },
                React.createElement(PriceAndSubscribeButton, { tier: tier }))) : !isOwner ? (React.createElement(UserWatchGhostButton, { author: deviation.author })) : null;
    }
    var typeAndItemIds = useMemo(function () { return ({
        typeid: getDeviationResourceType(deviation),
        itemid: deviation.deviationId,
    }); }, [deviation]);
    var thumbActions = useMemo(function () { return __spreadArray(__spreadArray([], extraActions, true), [
        deviation.isPublished && {
            value: 'goToPage',
            label: t('deviationview.feed.cardHeader.goToPage', {
                pageType: typeName,
            }),
            link: getDeviationUrl(deviation),
            onClick: onDuperbrowseClick,
            biData: __assign({ evid: BiEventType.WATCH_MENU_ITEM_SELECTION, menu_item: 'goToPage' }, typeAndItemIds),
        },
        isReportable && {
            value: 'report',
            label: t('deviationview.feed.cardHeader.report'),
            onClick: showReportModal,
            biData: __assign({ evid: BiEventType.WATCH_MENU_ITEM_SELECTION, menu_item: 'report' }, typeAndItemIds),
        },
        isOwner && {
            value: 'delete',
            label: t('deviationview.feed.cardHeader.delete'),
            onClick: function () { return setShowingConfirmation(true); },
            biData: __assign({ evid: BiEventType.WATCH_MENU_ITEM_SELECTION, menu_item: 'delete' }, typeAndItemIds),
        },
    ], false); }, [
        deviation,
        typeAndItemIds,
        extraActions,
        isReportable,
        showReportModal,
        isOwner,
        t,
        typeName,
        onDuperbrowseClick,
    ]);
    return (React.createElement("div", { className: classnames(s['root'], className) },
        deviation.author && (React.createElement(UserAvatar, { className: s['avatar'], user: deviation.author, withTooltip: true, size: 32, relatedItemBiData: typeAndItemIds })),
        React.createElement("div", { className: s['left-block'] },
            deviation.author && (React.createElement(UserLink, { className: s['username'], user: deviation.author, withTooltip: true, relatedItemBiData: {
                    typeid: deviation.typeId,
                    itemid: deviation.deviationId,
                } })),
            React.createElement(DeviationLink, { className: s['timestamp'], deviation: deviation },
                React.createElement(Timestamp, { timestamp: deviation.publishedTime, showFullLengthOnMobile: true }))),
        React.createElement("div", { className: s['right-block'], ref: duperRef },
            watchOrSubscribeButton,
            thumbActions.length > 0 && (React.createElement(ThumbActions, { className: s['thumb-actions'], deviation: deviation, actions: thumbActions },
                React.createElement(ButtonB5, { icon: EllipsisIcon, iconProps: {
                        size: IconSize.SMALL,
                    } })))),
        isOwner && (React.createElement(ConfirmationModal, { isOpen: isShowingConfirmation, title: t('deviationview.feed.cardHeader.deleteConfirmation.confirm', {
                type: typeName,
            }), text: t('deviationview.feed.cardHeader.deleteConfirmation.cantGoBack'), confirmBtnLabel: t('deviationview.feed.cardHeader.deleteConfirmation.delete'), icon: React.createElement(PremiumTrash, null), confirmBtnType: "danger", onConfirm: function () {
                logBiEvent(__assign({ evid: BiEventType.DELETE_DEVIATION_CONFIRM_CLICK }, typeAndItemIds));
                setShowingConfirmation(false);
                deleteDeviation();
            }, onCancel: function () { return setShowingConfirmation(false); } }))));
};
FeedCardHeader.displayName = 'FeedCardHeader';
export default FeedCardHeader;
//# sourceMappingURL=FeedCardHeader.js.map